import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Menu,
  X,
  Phone,
  Mail,
  MessageCircle,
  Headphones,
  Users,
  FileText,
  Wifi,
  Shield,
  BarChart3,
  CheckCircle2,
  ArrowRight,
  Award,
  Cloud,
  Zap,
  ThumbsUp,
  Target,
  MapPin
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = ({ scrollTo }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (scrollTo) {
      const timer = setTimeout(() => {
        const element = document.getElementById(scrollTo);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [scrollTo]);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });

  const company = {
    name: 'infra.solution',
    phone: '+55 11 989213980',
    email: 'contato@infrasolutioncorp.com',
    whatsapp: '5511914940258'
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contacts`, formData);

      if (response.data.success) {
        alert('Mensagem enviada com sucesso! Entraremos em contato em breve.');
        setFormData({ name: '', email: '', phone: '', company: '', message: '' });
      }
    } catch (error) {
      console.error('Erro ao enviar mensagem:', error);
      alert('Erro ao enviar mensagem. Por favor, tente novamente ou entre em contato pelo WhatsApp.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  const openWhatsApp = () => {
    window.open(`https://wa.me/${company.whatsapp}?text=Olá! Gostaria de conhecer os serviços da infra.solution`, '_blank');
  };

  const services = [
    {
      icon: Headphones,
      title: 'Suporte Técnico de TI',
      description: 'Problemas com computador, internet, impressora ou sistema? Nossa equipe resolve rapidamente, de forma remota ou presencial — sem estresse para você.',
      highlight: 'Atendimento rápido e humano'
    },
    {
      icon: Users,
      title: 'Outsourcing de TI',
      description: 'Tenha uma equipe completa de TI sem precisar contratar. A Infra.Solution cuida de toda a gestão tecnológica da sua empresa com mais eficiência e menos custo.',
      highlight: 'TI completa sem CLT'
    },
    {
      icon: FileText,
      title: 'Contratos de TI',
      description: 'Planos mensais com suporte ilimitado, manutenção preventiva e atendimento prioritário. Previsibilidade de custo e tranquilidade para o seu negócio.',
      highlight: 'Custo fixo mensal'
    },
    {
      icon: Wifi,
      title: 'Infraestrutura de Redes',
      description: 'Configuração e manutenção de redes cabeadas e Wi-Fi, servidores, firewalls e VPN. Internet rápida e segura para toda a empresa.',
      highlight: 'Rede sempre estável'
    },
    {
      icon: Shield,
      title: 'Segurança Digital',
      description: 'Proteja os dados da sua empresa contra vírus, ataques e vazamentos. Soluções de backup, antivírus corporativo e políticas de segurança.',
      highlight: 'Seus dados protegidos'
    },
    {
      icon: BarChart3,
      title: 'Consultoria em TI',
      description: 'Não sabe por onde começar? Analisamos sua operação e indicamos as melhores soluções tecnológicas para fazer seu negócio crescer.',
      highlight: 'Decisões mais inteligentes'
    }
  ];

  const microsoft365Features = [
    { icon: Mail, title: 'E-mail Profissional', description: 'Outlook com domínio próprio para toda a equipe.' },
    { icon: Users, title: 'Teams & Reuniões', description: 'Videoconferências e colaboração em tempo real.' },
    { icon: FileText, title: 'Office Completo', description: 'Word, Excel, PowerPoint na nuvem e no PC.' },
    { icon: Shield, title: 'Segurança Avançada', description: 'Proteção contra phishing, spam e ransomware.' },
    { icon: Users, title: 'Trabalho em Equipe', description: 'SharePoint e OneDrive para compartilhar arquivos.' },
    { icon: Cloud, title: '1TB de Armazenamento', description: 'Por usuário, com backup automático na nuvem.' }
  ];

  const plans = [
    {
      name: 'Essencial',
      subtitle: 'Para empresas de até 10 usuários.',
      price: 'Sob consulta',
      priceSubtitle: 'Personalizado para sua realidade',
      features: [
        'Suporte remoto ilimitado',
        'Atendimento em horário comercial',
        'Monitoramento básico de rede',
        'Antivírus corporativo gerenciado',
        'Relatório mensal'
      ],
      highlighted: false
    },
    {
      name: 'Profissional',
      subtitle: 'Para empresas de 10 a 50 usuários.',
      price: 'Sob consulta',
      priceSubtitle: 'Personalizado para sua realidade',
      features: [
        'Tudo do plano Essencial',
        'Suporte presencial incluso',
        'Microsoft 365 gerenciado',
        'Backup em nuvem',
        'Gestão de ativos de TI',
        'SLA de atendimento garantido'
      ],
      highlighted: true,
      badge: 'MAIS ESCOLHIDO'
    },
    {
      name: 'Corporativo',
      subtitle: 'Para empresas com 50+ usuários.',
      price: 'Sob consulta',
      priceSubtitle: 'Personalizado para sua realidade',
      features: [
        'Tudo do plano Profissional',
        'Analista dedicado de TI',
        'Suporte 24/7',
        'Gestão completa de infraestrutura',
        'Projetos de TI inclusos',
        'Reunião estratégica mensal'
      ],
      highlighted: false
    }
  ];

  const aboutFeatures = [
    { icon: Zap, title: 'Agilidade', description: 'Resolvemos problemas rapidamente para não travar sua operação.' },
    { icon: ThumbsUp, title: 'Confiança', description: 'Transparência total no que fazemos e no que cobramos.' },
    { icon: Award, title: 'Expertise', description: 'Equipe certificada Microsoft com experiência comprovada.' },
    { icon: MapPin, title: 'Presença Local', description: 'Atendimento presencial e remoto no ABC Paulista - SP e região.' }
  ];

  return (
    <div className="min-h-screen bg-[#0a1628]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#0a1628]/95 backdrop-blur-md border-b border-gray-800/50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-28">
            {/* Logo */}
            <div className="flex items-center cursor-pointer" onClick={() => scrollToSection('home')}>
              <img 
                src="https://customer-assets.emergentagent.com/job_outsource-it-experts/artifacts/nh97p7l3_Infra%20-%20logo.png" 
                alt="infra.solution Technology" 
                className="h-24 w-auto"
              />
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-8">
              <button onClick={() => scrollToSection('home')} className="text-gray-300 hover:text-[#10b981] transition-colors text-sm">
                Início
              </button>
              <button onClick={() => scrollToSection('services')} className="text-gray-300 hover:text-[#10b981] transition-colors text-sm">
                Serviços
              </button>
              <button onClick={() => scrollToSection('microsoft')} className="text-gray-300 hover:text-[#10b981] transition-colors text-sm">
                Microsoft 365
              </button>
              <button onClick={() => scrollToSection('plans')} className="text-gray-300 hover:text-[#10b981] transition-colors text-sm">
                Planos
              </button>
              <button onClick={() => scrollToSection('about')} className="text-gray-300 hover:text-[#10b981] transition-colors text-sm">
                Sobre
              </button>
              <Button onClick={openWhatsApp} className="bg-[#10b981] hover:bg-[#059669] text-white text-sm px-6">
                <Phone className="w-4 h-4 mr-2" />
                Fale Conosco
              </Button>
            </nav>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden text-white"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-[#0f1a2e] border-t border-gray-800/50">
            <div className="container mx-auto px-4 py-4 space-y-3">
              <button onClick={() => scrollToSection('home')} className="block w-full text-left py-2 text-gray-300 hover:text-[#10b981] transition-colors">
                Início
              </button>
              <button onClick={() => scrollToSection('services')} className="block w-full text-left py-2 text-gray-300 hover:text-[#10b981] transition-colors">
                Serviços
              </button>
              <button onClick={() => scrollToSection('microsoft')} className="block w-full text-left py-2 text-gray-300 hover:text-[#10b981] transition-colors">
                Microsoft 365
              </button>
              <button onClick={() => scrollToSection('plans')} className="block w-full text-left py-2 text-gray-300 hover:text-[#10b981] transition-colors">
                Planos
              </button>
              <button onClick={() => scrollToSection('about')} className="block w-full text-left py-2 text-gray-300 hover:text-[#10b981] transition-colors">
                Sobre
              </button>
              <Button onClick={openWhatsApp} className="w-full bg-[#10b981] hover:bg-[#059669] text-white">
                <Phone className="w-4 h-4 mr-2" />
                Fale Conosco
              </Button>
            </div>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section id="home" className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center space-y-8">
            {/* Badges */}
            <div className="flex flex-wrap justify-center gap-3 mb-6">
              <span className="inline-flex items-center px-4 py-2 rounded-full bg-[#10b981]/10 border border-[#10b981]/30 text-[#10b981] text-sm">
                <Award className="w-4 h-4 mr-2" />
                Microsoft Partner
              </span>
              <span className="inline-flex items-center px-4 py-2 rounded-full bg-[#10b981]/10 border border-[#10b981]/30 text-[#10b981] text-sm">
                <Cloud className="w-4 h-4 mr-2" />
                Soluções Cloud
              </span>
              <span className="inline-flex items-center px-4 py-2 rounded-full bg-[#10b981]/10 border border-[#10b981]/30 text-[#10b981] text-sm">
                <Headphones className="w-4 h-4 mr-2" />
                Suporte 24/7
              </span>
            </div>

            {/* Title */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight">
              Sua empresa <span className="text-[#10b981]">sempre funcionando</span>,<br />
              sem preocupações com TI.
            </h1>

            {/* Subtitle */}
            <p className="text-xl text-gray-400 max-w-3xl mx-auto leading-relaxed">
              Suporte técnico especializado, outsourcing de TI e contratos corporativos com foco em <strong className="text-white">Microsoft 365</strong>. Para pequenas e médias empresas do <strong className="text-white">ABC Paulista - SP</strong> e região que querem tecnologia de alto nível sem complicação.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
              <Button
                onClick={openWhatsApp}
                size="lg"
                className="bg-[#10b981] hover:bg-[#059669] text-white text-lg px-8 py-6"
              >
                Solicitar Proposta Grátis
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button
                onClick={() => scrollToSection('services')}
                size="lg"
                variant="outline"
                className="border-gray-700 text-gray-300 hover:bg-gray-800/50 text-lg px-8 py-6"
              >
                Conhecer os Serviços
              </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-8 pt-12 max-w-2xl mx-auto">
              <div>
                <div className="text-3xl sm:text-4xl font-bold text-[#10b981]">100+</div>
                <div className="text-sm text-gray-400 mt-1">Clientes Atendidos</div>
              </div>
              <div>
                <div className="text-3xl sm:text-4xl font-bold text-[#10b981]">15 anos</div>
                <div className="text-sm text-gray-400 mt-1">de Experiência</div>
              </div>
              <div>
                <div className="text-3xl sm:text-4xl font-bold text-[#10b981]">98%</div>
                <div className="text-sm text-gray-400 mt-1">Satisfação dos Clientes</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <span className="text-[#10b981] text-sm font-semibold tracking-wide uppercase">O que fazemos</span>
            <h2 className="text-4xl font-bold text-white mt-4 mb-6">Tudo que sua empresa precisa em TI</h2>
            <p className="text-xl text-gray-400">
              Soluções completas para manter sua operação funcionando sem interrupções.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => {
              const IconComponent = service.icon;
              return (
                <div
                  key={index}
                  className="bg-[#0f1a2e] border border-gray-800/50 rounded-2xl p-8 hover:border-[#10b981]/50 transition-all"
                >
                  <div className="w-12 h-12 bg-[#10b981]/10 rounded-xl flex items-center justify-center mb-6">
                    <IconComponent className="w-6 h-6 text-[#10b981]" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3">{service.title}</h3>
                  <p className="text-gray-400 leading-relaxed mb-4">
                    {service.description}
                  </p>
                  <div className="flex items-center text-[#10b981] text-sm font-medium">
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    {service.highlight}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Microsoft 365 Section */}
      <section id="microsoft" className="py-20 px-4 sm:px-6 lg:px-8 bg-[#0f1a2e]/50">
        <div className="container mx-auto max-w-6xl">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-[#10b981] text-sm font-semibold tracking-wide uppercase">Especialistas Microsoft</span>
              <h2 className="text-4xl font-bold text-white mt-4 mb-6">
                Microsoft 365 para a <span className="text-[#10b981]">sua empresa crescer</span>
              </h2>
              <p className="text-xl text-gray-400 mb-8">
                Implantamos, configuramos e gerenciamos o Microsoft 365 do início ao fim. Sua equipe ganha produtividade, mobilidade e segurança — sem precisar entender de tecnologia.
              </p>
              <ul className="space-y-3 mb-8">
                {[
                  'Migração completa de e-mails e dados',
                  'Configuração de todos os dispositivos',
                  'Treinamento para sua equipe',
                  'Suporte técnico contínuo',
                  'Gerenciamento de licenças',
                  'Segurança e conformidade'
                ].map((item, index) => (
                  <li key={index} className="flex items-center text-gray-300">
                    <CheckCircle2 className="w-5 h-5 text-[#10b981] mr-3 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <Button onClick={openWhatsApp} className="bg-[#10b981] hover:bg-[#059669] text-white">
                Quero implantar o Microsoft 365
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              {microsoft365Features.map((feature, index) => {
                const IconComponent = feature.icon;
                return (
                  <div key={index} className="bg-[#0a1628] border border-gray-800/50 rounded-xl p-6">
                    <div className="w-10 h-10 bg-[#10b981]/10 rounded-lg flex items-center justify-center mb-4">
                      <IconComponent className="w-5 h-5 text-[#10b981]" />
                    </div>
                    <h4 className="text-white font-semibold mb-2">{feature.title}</h4>
                    <p className="text-gray-400 text-sm">{feature.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="plans" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <span className="text-[#10b981] text-sm font-semibold tracking-wide uppercase">Contratos de TI</span>
            <h2 className="text-4xl font-bold text-white mt-4 mb-6">Planos para todos os tamanhos de empresa</h2>
            <p className="text-xl text-gray-400">
              Escolha o plano ideal para sua empresa. Todos com suporte especializado e sem surpresas na mensalidade.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {plans.map((plan, index) => (
              <div
                key={index}
                className={`relative rounded-2xl p-8 ${
                  plan.highlighted
                    ? 'bg-[#0f1a2e] border-2 border-[#10b981]'
                    : 'bg-[#0f1a2e] border border-gray-800/50'
                }`}
              >
                {plan.badge && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-[#10b981] text-white text-xs font-bold px-4 py-1 rounded-full">
                      {plan.badge}
                    </span>
                  </div>
                )}
                <h3 className="text-2xl font-bold text-white mb-2">{plan.name}</h3>
                <p className="text-gray-400 mb-6">{plan.subtitle}</p>
                <div className="mb-6">
                  <div className="text-3xl font-bold text-[#10b981]">{plan.price}</div>
                  <div className="text-sm text-gray-400 mt-1">{plan.priceSubtitle}</div>
                </div>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start text-gray-300 text-sm">
                      <CheckCircle2 className="w-4 h-4 text-[#10b981] mr-3 flex-shrink-0 mt-0.5" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Button
                  onClick={openWhatsApp}
                  className={`w-full ${
                    plan.highlighted
                      ? 'bg-[#10b981] hover:bg-[#059669] text-white'
                      : 'bg-transparent border border-[#10b981] text-[#10b981] hover:bg-[#10b981]/10'
                  }`}
                >
                  Solicitar Proposta
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 px-4 sm:px-6 lg:px-8 bg-[#0f1a2e]/50">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <span className="text-[#10b981] text-sm font-semibold tracking-wide uppercase">Sobre nós</span>
            <h2 className="text-4xl font-bold text-white mt-4 mb-6">
              Seu parceiro tecnológico <span className="text-[#10b981]">de confiança</span>
            </h2>
          </div>
          
          <div className="max-w-3xl mx-auto mb-12">
            <p className="text-gray-400 text-lg leading-relaxed mb-6">
              A <strong className="text-white">Infra.Solution Technology</strong> nasceu com uma missão clara: fazer com que empresas de todos os tamanhos tenham acesso a uma TI de qualidade, sem complicação e com custo justo.
            </p>
            <p className="text-gray-400 text-lg leading-relaxed">
              Somos especialistas em infraestrutura de TI e soluções Microsoft. Atuamos como o departamento de tecnologia da sua empresa — cuidando de tudo para que você possa focar no que realmente importa: <strong className="text-white">o seu negócio.</strong>
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {aboutFeatures.map((feature, index) => {
              const IconComponent = feature.icon;
              return (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 bg-[#10b981]/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <IconComponent className="w-8 h-8 text-[#10b981]" />
                  </div>
                  <h4 className="text-white font-bold text-lg mb-2">{feature.title}</h4>
                  <p className="text-gray-400 text-sm">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-12">
            <span className="text-[#10b981] text-sm font-semibold tracking-wide uppercase">Pronto para começar?</span>
            <h2 className="text-4xl font-bold text-white mt-4 mb-6">Fale com a nossa equipe hoje mesmo</h2>
            <p className="text-xl text-gray-400">
              Avaliamos as necessidades da sua empresa gratuitamente e apresentamos a solução ideal. Sem compromisso.
            </p>
            <div className="flex items-center justify-center text-gray-400 text-sm mt-3">
              <MapPin className="w-4 h-4 mr-1 text-[#10b981]" />
              Atendemos empresas no ABC Paulista - SP e região metropolitana
            </div>
          </div>

          <Card className="bg-[#0f1a2e] border-gray-800">
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-4">
                  <Input
                    name="name"
                    placeholder="Seu nome"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="bg-[#0a1628] border-gray-700 text-white placeholder:text-gray-500 focus:border-[#10b981]"
                  />
                  <Input
                    name="email"
                    type="email"
                    placeholder="Seu email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="bg-[#0a1628] border-gray-700 text-white placeholder:text-gray-500 focus:border-[#10b981]"
                  />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Input
                    name="phone"
                    placeholder="Telefone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="bg-[#0a1628] border-gray-700 text-white placeholder:text-gray-500 focus:border-[#10b981]"
                  />
                  <Input
                    name="company"
                    placeholder="Empresa"
                    value={formData.company}
                    onChange={handleInputChange}
                    className="bg-[#0a1628] border-gray-700 text-white placeholder:text-gray-500 focus:border-[#10b981]"
                  />
                </div>
                <Textarea
                  name="message"
                  placeholder="Sua mensagem"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  rows={5}
                  className="bg-[#0a1628] border-gray-700 text-white placeholder:text-gray-500 focus:border-[#10b981] resize-none"
                />
                <Button
                  type="submit"
                  className="w-full bg-[#10b981] hover:bg-[#059669] text-white text-lg py-6 disabled:opacity-50"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Enviando...' : 'Enviar Mensagem'}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </form>
              <p className="text-center text-sm text-gray-400 mt-6">
                Resposta em menos de 1 hora em horário comercial
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#060d1a] border-t border-gray-800/50 py-8 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto max-w-6xl text-center">
          <div className="mb-6">
            <img 
              src="https://customer-assets.emergentagent.com/job_outsource-it-experts/artifacts/nh97p7l3_Infra%20-%20logo.png" 
              alt="infra.solution Technology" 
              className="h-20 w-auto mx-auto mb-4"
            />
          </div>
          <div className="flex items-center justify-center text-gray-400 text-sm mb-2">
            <MapPin className="w-4 h-4 mr-1 text-[#10b981]" />
            ABC Paulista - SP | Atendimento presencial e remoto
          </div>
          <p className="text-gray-400 text-sm">
            © {new Date().getFullYear()} infra.solution Technology. Todos os direitos reservados.
          </p>
        </div>
      </footer>

      {/* WhatsApp Floating Button */}
      <button
        onClick={openWhatsApp}
        className="fixed bottom-6 right-6 w-16 h-16 bg-[#10b981] hover:bg-[#059669] rounded-full shadow-2xl flex items-center justify-center z-50 transition-all hover:scale-110"
        aria-label="Abrir WhatsApp"
      >
        <MessageCircle className="w-8 h-8 text-white" />
      </button>
    </div>
  );
};

export default Home;